import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useUIStore } from "@/stores/useUIStore";

const STORAGE_KEY = "wexare-language";
// Must list every locale the switcher offers. `pt` was missing, so a
// Portuguese switch in one tab was dropped on the floor by every other tab.
const SUPPORTED_LANGS = ["en", "es", "pt"] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];

function isSupportedLang(lang: string): lang is SupportedLang {
  return SUPPORTED_LANGS.includes(lang as SupportedLang);
}

function LanguageProvider({ children }: { children: ReactNode }) {
  const language = useUIStore((s) => s.language);
  const setLanguage = useUIStore((s) => s.setLanguage);
  const { i18n } = useTranslation();
  // Tracks whether the initial sync has happened.
  // Starts false so the sync effect skips the first render.
  const ready = useRef(false);

  // Apply the already-correct store value to i18next + DOM on mount
  useEffect(() => {
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
    ready.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only: later changes go through the sync effect below
  }, []);

  // Sync subsequent language changes → i18next + DOM + localStorage
  useEffect(() => {
    if (!ready.current) return;
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
    localStorage.setItem(STORAGE_KEY, language);
  }, [language, i18n]);

  // Cross-tab sync via storage event
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY && e.newValue && isSupportedLang(e.newValue)) {
        setLanguage(e.newValue);
      }
    }
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [setLanguage]);

  return <>{children}</>;
}

export { LanguageProvider };
